import React from "react";

// FindingRow — one icon-led KEY FINDINGS line (V6 §E.1). A clickable row when it
// drills, a plain row when it does not: a finding that resolves to a set with no
// ids has nowhere to go and must not pretend to be clickable (Part B principle 3).
//
// The icon's colour is the only place a finding carries meaning, so it maps to the
// C.2 treatments — leaking (money/people out), fixable (ours to fix), good (a win
// or something callable now). The row text is rendered verbatim; KeyFindings
// assembles it from already-fetched RPC numbers and backend drop-label fragments.

// Each tone is a coloured icon on a matching tinted chip (user ruling 2026-09-04:
// "add colours, make it better") — the C.2 semantics carried in colour: leaking =
// money/people out (red), fixable = ours to fix (amber), good = a win / callable
// now (emerald), brand = neutral (blue).
const ICON_TONES = {
  leaking: "bg-red-50 text-red-500 dark:bg-red-500/15 dark:text-red-400",
  fixable: "bg-amber-50 text-amber-500 dark:bg-amber-500/15 dark:text-amber-400",
  good: "bg-emerald-50 text-emerald-500 dark:bg-emerald-500/15 dark:text-emerald-400",
  brand: "bg-[#32B1FF]/10 text-[#32B1FF] dark:bg-[#32B1FF]/15",
};

const Svg = ({ children }) => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

// Glyphs keyed by finding kind. Named rather than passed in so KeyFindings stays a
// data-assembly component and the icon set lives in one place.
export const FINDING_ICONS = {
  alert: (
    <Svg>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Svg>
  ),
  dollar: (
    <Svg>
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </Svg>
  ),
  phone: (
    <Svg>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </Svg>
  ),
  clock: (
    <Svg>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Svg>
  ),
};

const FindingRow = ({ icon = "alert", tone = "brand", text, onClick }) => {
  const glyph = (
    <span
      className={`shrink-0 inline-flex items-center justify-center rounded-md p-1 ${ICON_TONES[tone] || ICON_TONES.brand}`}
      aria-hidden="true"
    >
      {FINDING_ICONS[icon] || FINDING_ICONS.alert}
    </span>
  );
  const body = (
    <>
      {glyph}
      <span className="text-sm leading-snug text-gray-700 dark:text-gray-300 transition-colors group-hover:text-[#32B1FF]">{text}</span>
    </>
  );
  if (!onClick) {
    return (
      <li data-testid="finding-row" data-clickable="false" className="flex items-center gap-2 py-0.5">
        {body}
      </li>
    );
  }
  return (
    <li data-testid="finding-row" data-clickable="true">
      {/* Rows hug their single sentence (user ruling 2026-09-04: tighter spacing) —
          deliberately below the 44px touch floor the rest of the page holds, the
          same density-over-floor decision the retired glass KeyFindings panel made.
          The row is still a full-width, keyboard-reachable control with a focus ring. */}
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full items-center gap-2 text-left rounded-md px-1 -mx-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
      >
        {body}
      </button>
    </li>
  );
};

export default FindingRow;
